<template>
    <div class="kv-card" :class="[
        variantClass,
        densityClass,
        {
            'kv-collapsible': collapsible,
            'kv-open': isOpenComputed,
            'kv-no-header': !hasHeaderSlots
        }
    ]">
        <div class="kv-card-header" v-if="$slots.title || $slots.subtitle">
            <div class="kv-card-header-inner">
                <slot name="title" />
                <span v-if="$slots.subtitle" class="kv-card-subtitle">
                    <slot name="subtitle" />
                </span>
            </div>

            <div v-if="collapsible" class="collapse-icon" @click="toggle">
                <Icon iconClass="ui-chevron-down" :class="{ rotated: isOpenComputed }" />
            </div>
        </div>

        <div class="kv-card-content-wrapper" v-if="hasActions || $slots.text">
            <div class="kv-expandable-content">
                <div class="kv-card-content-inner">
                    <div class="kv-card-content" :class="[contentClass]">
                        <slot name="text" />
                    </div>

                    <div v-if="hasActions" class="kv-card-actions mt-3">
                        <slot name="actions" />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="$slots.footer" class="kv-card-footer">
            <slot name="footer" />
        </div>
    </div>
</template>

<script>
export default {
    name: "KvCard",
    model: {
        prop: 'open',
        event: 'input'
    },
    props: {
        collapsible: { type: Boolean, default: false },
        variant: {
            type: String,
            default: "outlined",
        },
        open: {
            type: Boolean,
            default: undefined,
        },
        density: {
            type: String,
            default: "default",
            validator: v => ["compact", "default", "comfortable"].includes(v)
        },
        defaultOpen: {
            type: Boolean,
            default: false,
        },
        contentClass: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isOpen: this.collapsible && this.defaultOpen,
            hasHeaderSlots: false,
            hasActions: false,

        }
    },
    created() {
        this.hasHeaderSlots =
            !!this.$slots.title ||
            !!this.$slots.subtitle ||
            !!this.$slots.header;
        this.hasActions = !!this.$slots.actions;
    },
    computed: {
        variantClass() {
            return `kv-${this.variant}-card`;
        },
        densityClass() {
            return `kv-density-${this.density}`;
        },
        isOpenComputed: {
            get() {
                if (this.open !== undefined) {
                    return this.open;
                }
                return this.isOpen;
            },
            set(value) {
                if (this.open !== undefined) {
                    this.$emit("input", value);
                } else {
                    this.isOpen = value;
                }
            }
        }
    },
    methods: {
        toggle() {
            this.isOpenComputed = !this.isOpenComputed;
        },
    }
};
</script>

<style lang="less">
// Breakpoint para mobile
@mobile-breakpoint: 768px;

.kv-card {
    border: 1px solid #E5E7EB;
    border-radius: 16px;
    background-color: #FCFCFD;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    justify-content: center;

    >.kv-card-content-wrapper {
        height: 100%;
        display: flex;
        align-items: center;

        >.kv-expandable-content {
            width: 100%;
        }
    }

    &.kv-collapsible .kv-card-content-wrapper {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.25s ease-in-out;
        align-items: initial;

        >.kv-expandable-content {
            overflow: hidden;
        }
    }

    &.kv-collapsible>.kv-card-header>.kv-card-header-inner {
        padding-right: 48px;
    }

    &.kv-collapsible.kv-density-compact>.kv-card-header>.kv-card-header-inner {
        padding-right: 24px;
    }

    &.kv-open {
        .kv-card-content-wrapper {
            grid-template-rows: 1fr;
        }
    }

    // 1. Compact
    &.kv-density-compact {
        >.kv-card-header {
            padding: 12px;
        }

        >.kv-card-content-wrapper>.kv-expandable-content>.kv-card-content-inner {
            padding: 0 12px 12px;
        }

        >.kv-card-header .collapse-icon {
            padding: 12px;
        }

        >.kv-card-footer {
            padding: 0 12px 12px;
        }
    }

    // 2. Default
    &.kv-density-default {
        >.kv-card-header {
            padding: 24px;

            @media (max-width: @mobile-breakpoint) {
                padding: 16px;
            }
        }

        >.kv-card-content-wrapper>.kv-expandable-content>.kv-card-content-inner {
            padding: 0 24px 24px;

            @media (max-width: @mobile-breakpoint) {
                padding: 0 16px 16px;
            }
        }

        >.kv-card-header .collapse-icon {
            @media (max-width: @mobile-breakpoint) {
                padding: 16px;
            }
        }

        >.kv-card-footer {
            padding: 0 24px 24px;

            @media (max-width: @mobile-breakpoint) {
                padding: 0 16px 16px;

            }
        }
    }

    // 3. Comfortable
    &.kv-density-comfortable {
        >.kv-card-header {
            padding: 32px;

            @media (max-width: @mobile-breakpoint) {
                padding: 20px;
            }
        }

        >.kv-card-content-wrapper>.kv-expandable-content>.kv-card-content-inner {
            padding: 0 32px 32px;

            @media (max-width: @mobile-breakpoint) {
                padding: 0 20px 20px;
            }
        }

        >.kv-card-header .collapse-icon {
            padding: 32px;

            @media (max-width: @mobile-breakpoint) {
                padding: 20px;
            }
        }

        >.kv-card-footer {
            padding: 0 32px 32px;

            @media (max-width: @mobile-breakpoint) {
                padding: 0 20px 20px;

            }
        }
    }

    &.kv-collapsible>.kv-card-header {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        position: relative;

        >.collapse-icon {
            height: 100%;
            padding: 24px; // Fallback Default
            display: flex;
            align-items: center;
            cursor: pointer;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: 0;
        }
    }

    // Lógica para quando não tem header (Padding Top)
    &.kv-no-header>.kv-card-content-wrapper>.kv-expandable-content>.kv-card-content-inner {
        padding-top: 24px;

        @media (max-width: @mobile-breakpoint) {
            padding-top: 16px;
        }
    }

    &.kv-no-header.kv-density-compact>.kv-card-content-wrapper>.kv-expandable-content>.kv-card-content-inner {
        padding-top: 12px;
    }

    &.kv-no-header.kv-density-comfortable>.kv-card-content-wrapper>.kv-expandable-content>.kv-card-content-inner {
        padding-top: 32px;

        @media (max-width: @mobile-breakpoint) {
            padding-top: 20px;
        }
    }

    .kv-card-subtitle {
        color: #6C737F;
        font-size: 12px;
    }

    .ui-chevron-down {
        transition: transform 0.2s ease-in-out;

        &.rotated {
            transform: rotate(180deg);
        }
    }

    &.kv-tonal-card {
        background-color: #F3F4F6;
    }

    &.kv-soft-card {
        background-color: #F9FAFB;
    }

    &.kv-text-card {
        background-color: transparent;
        border-color: transparent;
    }
}
</style>